import { API_BASE_URL, BACKEND_BASE_URL } from '../config/api';
import { graphqlRequest } from './graphqlClient';
import type { User, UserRole } from '../types';

interface UserServiceConfig {
  baseUrl: string;
  tokenKey: string;
  userKey: string;
}

export interface NotifyPublisherPayload {
  publisherId: string;
  message: string;
  subject?: string;
  publisherEmail?: string;
  senderName?: string;
  senderEmail?: string;
}

export interface NotifyPublisherResult {
  message: string;
  delivered: boolean;
  mailtoUrl?: string;
}

const defaultUserServiceConfig: UserServiceConfig = {
  baseUrl: API_BASE_URL,
  tokenKey: 'haapa.authToken',
  userKey: 'haapa.currentUser',
};

const GET_USERS_QUERY = `
  query {
    getUsers {
      id
      firstname
      lastname
      username
      email
      telephone
      phone
      role
      country
      profile
    }
  }
`;

class UserService {
  private baseUrl: string;
  private tokenKey: string;
  private userKey: string;

  constructor(config = defaultUserServiceConfig) {
    this.baseUrl = config.baseUrl;
    this.tokenKey = config.tokenKey;
    this.userKey = config.userKey;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string | undefined {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return undefined;
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private getFirstString(...values: unknown[]): string | undefined {
    for (const value of values) {
      const resolved = this.asString(value);
      if (resolved) {
        return resolved;
      }
    }
    return undefined;
  }

  private normalizeAssetUrl(value: unknown): string | undefined {
    const rawValue = this.getFirstString(value);
    if (!rawValue) {
      return undefined;
    }

    if (/^(https?:|data:|blob:)/i.test(rawValue)) {
      return rawValue;
    }

    const normalizedPath = rawValue.startsWith('/') ? rawValue : `/${rawValue}`;
    return `${BACKEND_BASE_URL}${normalizedPath}`;
  }

  private normalizeRole(value: unknown): UserRole {
    if (value === 'creator') {
      return 'vendor';
    }

    if (value === 'admin' || value === 'manager' || value === 'vendor' || value === 'buyer') {
      return value;
    }

    if (value === 'customer') {
      return 'buyer';
    }

    return 'buyer';
  }

  private normalizeProvider(value: unknown): User['provider'] {
    if (value === 'google' || value === 'apple' || value === 'email') {
      return value;
    }
    return 'email';
  }

  private getStoredUserRecord(): Record<string, unknown> {
    if (typeof window === 'undefined') {
      return {};
    }

    const raw = window.localStorage.getItem(this.userKey);
    if (!raw) {
      return {};
    }

    try {
      return this.asRecord(JSON.parse(raw));
    } catch {
      return {};
    }
  }

  private buildHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      Accept: '*/*',
    };

    if (typeof window === 'undefined') {
      return headers;
    }

    const token = window.localStorage.getItem(this.tokenKey);
    const storedUser = this.getStoredUserRecord();

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const userId = this.asString(storedUser.id);
    if (userId) {
      headers['x-user-id'] = userId;
    }

    const role = this.asString(storedUser.role);
    if (role) {
      headers['x-user-role'] = role;
    }

    const audience = this.getFirstString(storedUser.audience, storedUser.audienceProfile);
    if (audience) {
      headers['x-user-audience'] = audience;
    }

    return headers;
  }

  private toAbsoluteUrl(path: string): string {
    const sanitizedBaseUrl = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
    const sanitizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${sanitizedBaseUrl}${sanitizedPath}`;
  }

  private toErrorMessage(payload: unknown, fallback: string): string {
    const payloadRecord = this.asRecord(payload);
    const dataRecord = this.asRecord(payloadRecord.data);
    return this.getFirstString(payloadRecord.message, payloadRecord.error, dataRecord.message, dataRecord.error) ?? fallback;
  }

  private async request(path: string, options?: { method?: string; body?: unknown; headers?: HeadersInit }): Promise<unknown> {
    const method = options?.method?.toUpperCase() || 'GET';
    const headers: Record<string, string> = {
      ...(this.buildHeaders() as Record<string, string>),
      ...(options?.headers as Record<string, string> | undefined),
    };

    const init: RequestInit = {
      method,
      headers,
    };

    if (options && 'body' in options) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      init.body = JSON.stringify(options.body ?? {});
    }

    const response = await fetch(this.toAbsoluteUrl(path), init);

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const payloadRecord = this.asRecord(payload);
      if (response.status === 401) {
        throw new Error('Unauthorized. Please sign in again.');
      }
      if (response.status === 403) {
        throw new Error('Forbidden. Admin access is required for this action.');
      }

      const message = this.getFirstString(payloadRecord.message, payloadRecord.error) ?? `Request failed with status ${response.status}`;
      throw new Error(message);
    }

    return payload;
  }

  private resolveUserList(payload: unknown): unknown[] {
    const payloadRecord = this.asRecord(payload);
    const payloadData = 'data' in payloadRecord ? payloadRecord.data : payload;
    if (Array.isArray(payloadData)) {
      return payloadData;
    }

    const dataRecord = this.asRecord(payloadData);
    const candidates = [
      dataRecord.users,
      dataRecord.items,
      dataRecord.results,
      payloadRecord.users,
      payloadRecord.items,
      payloadRecord.results,
    ];

    for (const candidate of candidates) {
      const list = this.asArray(candidate);
      if (list.length > 0) {
        return list;
      }
    }

    return [];
  }

  private asBoolean(value: unknown): boolean | undefined {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
      if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    }
    return undefined;
  }

  private toBackendRole(role: UserRole): string {
    if (role === 'vendor') return 'creator';
    if (role === 'buyer') return 'customer';
    return role;
  }

  private normalizeUser(value: unknown): User | null {
    const userRecord = this.asRecord(value);
    const id = this.getFirstString(userRecord.id, userRecord._id, userRecord.userId);
    if (!id) {
      return null;
    }

    const firstname = this.getFirstString(userRecord.firstname, userRecord.firstName);
    const lastname = this.getFirstString(userRecord.lastname, userRecord.lastName);
    const combinedName = [firstname, lastname].filter(Boolean).join(' ');
    const email =
      this.getFirstString(userRecord.email, userRecord.username) ??
      `${id}@haapa.local`;
    const name =
      this.getFirstString(userRecord.name, userRecord.fullname, userRecord.fullName, combinedName, firstname, userRecord.username) ??
      'Reader';

    return {
      id,
      name,
      email: email.toLowerCase(),
      role: this.normalizeRole(userRecord.role),
      provider: this.normalizeProvider(userRecord.provider),
      phone: this.getFirstString(userRecord.phone, userRecord.phoneNumber, userRecord.telephone),
      country: this.getFirstString(userRecord.country),
      preferredLanguage: this.getFirstString(userRecord.preferredLanguage) as User['preferredLanguage'] | undefined,
      dateOfBirth: this.getFirstString(userRecord.dateOfBirth),
      profileImageUrl: this.normalizeAssetUrl(
        this.getFirstString(
          userRecord.profileImageUrl,
          userRecord.profileImage,
          userRecord.profile,
          userRecord.avatar,
          userRecord.imageUrl,
          userRecord.photoUrl,
          userRecord.file,
          userRecord.picture,
          userRecord.pictureFile,
        ),
      ),
      verified: this.asBoolean(userRecord.verified ?? userRecord.isVerified ?? userRecord.is_verified ?? userRecord.is_verified_account),
    };
  }

  private async fetchVendorsViaGraphQL(): Promise<User[]> {
    try {
      const data = await graphqlRequest<{ getUsers?: unknown[] }>(GET_USERS_QUERY, undefined, { requireAuth: false });
      const list = this.resolveUserList(data);
      const normalized = list
        .map((entry) => this.normalizeUser(entry))
        .filter((entry): entry is User => Boolean(entry))
        .filter((user) => user.role === 'vendor' || user.role === 'admin' || user.role === 'manager');
      const deduped = new Map<string, User>();
      normalized.forEach((user) => deduped.set(user.id, user));
      return [...deduped.values()];
    } catch (error) {
      console.error('GraphQL vendors fetch failed', error);
      return [];
    }
  }

  async getUsers(): Promise<User[]> {
    const storedUser = this.getStoredUserRecord();
    const role = this.normalizeRole(storedUser.role);
    
    // If not admin, don't even try to fetch user list to avoid 401/403 console errors
    if (role !== 'admin') {
      return [];
    }

    const path = '/users';

    try {
      const payload = await this.request(path);
      const normalizedUsers = this.resolveUserList(payload)
        .map((entry) => this.normalizeUser(entry))
        .filter((entry): entry is User => Boolean(entry));

      const deduped = new Map<string, User>();
      normalizedUsers.forEach((user) => {
        deduped.set(user.id, user);
      });
      return [...deduped.values()];
    } catch (error: unknown) {
      // Fail silently for user list enrichment to keep console clean
      return [];
    }
  }

  async assignUserRole(userId: string | number, role: UserRole): Promise<void> {
    const resolvedId = typeof userId === 'string' ? userId.trim() : String(userId);
    if (!resolvedId) {
      throw new Error('User is required.');
    }

    const backendRole = this.toBackendRole(role);

    try {
      await this.request(`/users/${encodeURIComponent(resolvedId)}/role`, {
        method: 'PATCH',
        body: { role: backendRole },
      });
      return;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      if (message.toLowerCase().includes('validation') || message.toLowerCase().includes('bad request')) {
        await this.request(`/users/${encodeURIComponent(resolvedId)}/role?role=${encodeURIComponent(backendRole)}`, { method: 'PATCH' });
        return;
      }
      throw error;
    }
  }

  async approveUser(userId: string | number): Promise<void> {
    const resolvedId = typeof userId === 'string' ? userId.trim() : String(userId);
    if (!resolvedId) {
      throw new Error('User is required.');
    }

    await this.request(`/users/${encodeURIComponent(resolvedId)}/approve`, { method: 'PATCH' });
  }

  async deleteUser(userId: string | number): Promise<void> {
    const resolvedId = typeof userId === 'string' ? userId.trim() : String(userId);
    if (!resolvedId) {
      throw new Error('User is required.');
    }

    await this.request(`/users/${encodeURIComponent(resolvedId)}`, { method: 'DELETE' });
  }

  async getVendors(): Promise<User[]> {
    // bn backend does not expose vendors; return empty to prevent 404 spam.
    return [];
  }

  async notifyPublisher(payload: NotifyPublisherPayload): Promise<NotifyPublisherResult> {
    const publisherId = payload.publisherId?.trim();
    const message = payload.message?.trim();
    if (!publisherId) {
      throw new Error('Publisher is required.');
    }
    if (!message) {
      throw new Error('Message is required.');
    }

    if (typeof window !== 'undefined') {
      const token = window.localStorage.getItem(this.tokenKey);
      if (!token) {
        throw new Error('Please login first to notify this publisher.');
      }
    }

    const storedUser = this.getStoredUserRecord();
    const senderName =
      payload.senderName?.trim() ||
      this.getFirstString(storedUser.name, storedUser.fullname, storedUser.fullName, storedUser.username) ||
      'HAAPA User';
    const senderEmail = payload.senderEmail?.trim() || this.getFirstString(storedUser.email);
    if (!senderEmail) {
      throw new Error('Please login first with a valid email before sending this message.');
    }

    const subject = payload.subject?.trim() || 'Publisher Notification';
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(message);
    const mailtoUrl = payload.publisherEmail?.trim()
      ? `mailto:${payload.publisherEmail.trim()}?subject=${encodedSubject}&body=${encodedBody}`
      : undefined;

    const endpointTemplate = ((import.meta.env.VITE_NOTIFY_PUBLISHER_ENDPOINT as string | undefined)?.trim() || '/api/vendors/:vendorId/contact');
    const endpointPath = endpointTemplate.replace(':vendorId', encodeURIComponent(publisherId));

    const endpoint = endpointPath.startsWith('http') ? endpointPath : this.toAbsoluteUrl(endpointPath);
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        ...this.buildHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ senderName, senderEmail, subject, message }),
    });

    let responsePayload: unknown = null;
    try {
      responsePayload = await response.json();
    } catch {
      responsePayload = null;
    }

    if (response.ok) {
      return {
        delivered: true,
        message: this.toErrorMessage(responsePayload, 'Message sent successfully.'),
      };
    }

    if (response.status === 401 || response.status === 403) {
      throw new Error('Please login first to notify this publisher.');
    }

    if (response.status === 404) {
      return {
        delivered: false,
        message: 'Notification API endpoint was not found. Opening your email app instead.',
        mailtoUrl,
      };
    }

    throw new Error(this.toErrorMessage(responsePayload, `Request failed with status ${response.status}`));
  }
}

export const userService = new UserService();
export { UserService };
